import React, { useState } from "react";
import { 
  FiPackage, 
  FiTrendingUp, 
  FiTrendingDown, 
  FiAlertTriangle,
  FiCheckCircle,
  FiEdit2,
  FiTrash2,
  FiSearch,
  FiBox,
  FiLayers,
  FiActivity,
  FiDollarSign,
  FiShoppingCart
} from "react-icons/fi";

import SectionHeader from "../components/ui/SectionHeader";
import StatusBadge from "../components/ui/StatusBadge";
import {
  useCreateStockItemMutation,
  useCreateStockMovementMutation,
  useDeleteStockItemMutation,
  useDeleteStockMovementMutation,
  useGetStockItemsQuery,
  useGetStockMovementsQuery,
  useUpdateStockItemMutation,
  useUpdateStockMovementMutation,
} from "../services/api";
import { getApiErrorMessage } from "../utils/apiError";

const MATERIAL_TYPES = ["paper", "vinyl", "fabric", "canvas", "foam", "finishing", "ink", "other"];
const PRICING_METHODS = [
  { value: "per_pc", label: "Per Piece (pc)" },
  { value: "per_meter", label: "Per Meter (m)" },
];

const MATERIAL_COLORS = {
  paper: { bg: "#dbeafe", color: "#1e40af" },
  vinyl: { bg: "#fce7f3", color: "#be185d" },
  fabric: { bg: "#e0e7ff", color: "#5b21b6" },
  canvas: { bg: "#fef3c7", color: "#b45309" },
  foam: { bg: "#dcfce7", color: "#15803d" },
  finishing: { bg: "#f3e8ff", color: "#7c3aed" },
  ink: { bg: "#fee2e2", color: "#b91c1c" },
  other: { bg: "#f3f4f6", color: "#4b5563" },
};

const MATERIAL_ICONS = {
  paper: "📄",
  vinyl: "🎨",
  fabric: "🧵",
  canvas: "🖼️",
  foam: "🔲",
  finishing: "✨",
  ink: "🖊️",
  other: "📦",
};

const EMPTY_ITEM = {
  name: "",
  category: "",
  material_type: "paper",
  pricing_method: "per_pc",
  unit: "",
  quantity: 0,
  min_level: 0,
  buy_price: 0,
  unit_cost: 0,
};

const EMPTY_MOVEMENT = {
  stock_item_id: "",
  movement_type: "IN",
  quantity: 1,
  unit_cost: 0,
  note: "",
};

const StockPage = () => {
  const { data } = useGetStockItemsQuery();
  const { data: movements } = useGetStockMovementsQuery();
  const [createMovement] = useCreateStockMovementMutation();
  const [updateMovement] = useUpdateStockMovementMutation();
  const [deleteMovement] = useDeleteStockMovementMutation();
  const [createStockItem] = useCreateStockItemMutation();
  const [updateStockItem] = useUpdateStockItemMutation();
  const [deleteStockItem] = useDeleteStockItemMutation();

  const [submitError, setSubmitError] = useState("");
  const [itemError, setItemError] = useState("");
  const [editingMovementId, setEditingMovementId] = useState(null);
  const [editingItemId, setEditingItemId] = useState(null);
  const [form, setForm] = useState(EMPTY_MOVEMENT);
  const [itemForm, setItemForm] = useState(EMPTY_ITEM);
  const [searchQuery, setSearchQuery] = useState("");
  const [isSubmittingItem, setIsSubmittingItem] = useState(false);
  const [isSubmittingMovement, setIsSubmittingMovement] = useState(false);

  const items = data?.data || [];
  const movementList = movements?.data || [];

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitError("");
    setIsSubmittingMovement(true);
    try {
      if (editingMovementId) {
        await updateMovement({ id: editingMovementId, ...form }).unwrap();
      } else {
        await createMovement(form).unwrap();
      }
      setEditingMovementId(null);
      setForm({ ...EMPTY_MOVEMENT, stock_item_id: items[0]?.id || "" });
    } catch (error) {
      setSubmitError(getApiErrorMessage(error, "Unable to save stock movement."));
    } finally {
      setIsSubmittingMovement(false);
    }
  };

  const handleItemSubmit = async (event) => {
    event.preventDefault();
    setItemError("");
    setIsSubmittingItem(true);
    try {
      const payload = {
        ...itemForm,
        unit_cost: Number(itemForm.buy_price || itemForm.unit_cost || 0),
      };
      if (editingItemId) {
        await updateStockItem({ id: editingItemId, ...payload }).unwrap();
      } else {
        await createStockItem(payload).unwrap();
      }
      setEditingItemId(null);
      setItemForm(EMPTY_ITEM);
    } catch (error) {
      setItemError(getApiErrorMessage(error, "Unable to save stock item."));
    } finally {
      setIsSubmittingItem(false);
    }
  };

  const pricingLabel = (method) =>
    PRICING_METHODS.find((p) => p.value === method)?.label || method;

  const filteredItems = items.filter((item) =>
    item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.category.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.material_type.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Calculate stats
  const totalValue = items.reduce((sum, item) => 
    sum + (Number(item.quantity) * Number(item.buy_price || item.unit_cost || 0)), 0
  );
  const lowStockCount = items.filter(item => item.low_stock).length;
  const totalItems = items.length;

  return (
    <div className="dashboard-page">
      <SectionHeader
        eyebrow="Stock Management"
        title="Track inventory, materials, and movement history"
      />

      {/* Summary Stats Cards */}
      <div className="stats-grid-modern" style={{ marginBottom: "2rem" }}>
        <div className="stat-card-modern stat-card-modern--primary">
          <div className="stat-card-modern__header">
            <span className="stat-card-modern__label">Total Inventory Value</span>
            <div className="stat-card-modern__icon" style={{ background: "rgba(255,255,255,0.2)" }}>
              <FiDollarSign style={{ color: "#fff" }} />
            </div>
          </div>
          <div className="stat-card-modern__value">{totalValue.toFixed(2)} ETB</div>
          <div className="stat-card-modern__footer">
            <span className="stat-card-modern__meta" style={{ color: "rgba(255,255,255,0.85)" }}>
              Current stock value
            </span>
          </div>
        </div>

        <div className="stat-card-modern">
          <div className="stat-card-modern__header">
            <span className="stat-card-modern__label">Total Items</span>
            <div className="stat-card-modern__icon" style={{ background: "#dbeafe", color: "#2563eb" }}>
              <FiBox />
            </div>
          </div>
          <div className="stat-card-modern__value" style={{ color: "#2563eb" }}>{totalItems}</div>
          <div className="stat-card-modern__footer">
            <span className="stat-card-modern__meta">Stock items</span>
          </div>
        </div>

        <div className="stat-card-modern">
          <div className="stat-card-modern__header">
            <span className="stat-card-modern__label">Low Stock Alerts</span>
            <div className="stat-card-modern__icon" style={{ 
              background: lowStockCount > 0 ? "#fee2e2" : "#dcfce7", 
              color: lowStockCount > 0 ? "#ef4444" : "#10b981" 
            }}>
              <FiAlertTriangle />
            </div>
          </div>
          <div className="stat-card-modern__value" style={{ color: lowStockCount > 0 ? "#ef4444" : "#10b981" }}>
            {lowStockCount}
          </div>
          <div className="stat-card-modern__footer">
            <span className="stat-card-modern__meta">Items need restock</span>
          </div>
        </div>

        <div className="stat-card-modern">
          <div className="stat-card-modern__header">
            <span className="stat-card-modern__label">Recent Movements</span>
            <div className="stat-card-modern__icon" style={{ background: "#f3e8ff", color: "#7c3aed" }}>
              <FiActivity />
            </div>
          </div>
          <div className="stat-card-modern__value" style={{ color: "#7c3aed" }}>{movementList.length}</div>
          <div className="stat-card-modern__footer">
            <span className="stat-card-modern__meta">Stock transactions</span>
          </div>
        </div>
      </div>

      <div className="dashboard-grid dashboard-grid--wide">
        {/* ── Inventory list ── */}
        <section className="panel" style={{ gridColumn: "span 2" }}>
          <div style={{ padding: "1.5rem", borderBottom: "1px solid var(--border)" }}>
            <h3 style={{ margin: "0 0 1rem 0", fontSize: "1.25rem", fontWeight: "700" }}>Inventory</h3>
            
            {/* Search Bar */}
            <div className="form-input-group" style={{ maxWidth: "400px" }}>
              <span className="form-input-group__icon"><FiSearch /></span>
              <input 
                className="form-input" 
                value={searchQuery} 
                onChange={(e) => setSearchQuery(e.target.value)} 
                placeholder="Search materials..." 
                style={{ padding: "0.75rem 0.75rem 0.75rem 2.75rem" }}
              />
            </div>
          </div>

          {/* Stock Items Grid */}
          <div className="gallery-admin-grid">
            {filteredItems.length === 0 ? (
              <div className="empty-state-modern" style={{ gridColumn: "1 / -1" }}>
                <FiPackage />
                <p>{searchQuery ? "No items found" : "No stock items yet"}</p>
              </div>
            ) : (
              filteredItems.map((item) => (
                <div 
                  key={item.id} 
                  className="gallery-admin-card"
                  style={{ 
                    borderLeft: item.low_stock ? "4px solid #ef4444" : "4px solid transparent",
                    background: item.low_stock ? "#fef2f2" : "var(--bg-base)"
                  }}
                >
                  {/* Item Header */}
                  <div style={{ 
                    padding: "1rem",
                    borderBottom: "1px solid var(--border)",
                    background: MATERIAL_COLORS[item.material_type]?.bg || "#f9fafb"
                  }}>
                    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "0.5rem", marginBottom: "0.5rem" }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <h4 style={{ 
                          margin: "0 0 0.25rem 0", 
                          fontSize: "1rem", 
                          fontWeight: "700",
                          color: "var(--text-primary)",
                          lineHeight: "1.3"
                        }}>
                          {item.name}
                        </h4>
                        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
                          <span style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "4px",
                            padding: "4px 10px",
                            borderRadius: "12px",
                            background: MATERIAL_COLORS[item.material_type]?.bg || "#f3f4f6",
                            color: MATERIAL_COLORS[item.material_type]?.color || "#4b5563",
                            fontSize: "0.7rem",
                            fontWeight: "600",
                            textTransform: "capitalize"
                          }}>
                            {MATERIAL_ICONS[item.material_type]} {item.material_type}
                          </span>
                          <span style={{
                            padding: "4px 10px",
                            borderRadius: "12px",
                            background: "rgba(0,0,0,0.05)",
                            fontSize: "0.7rem",
                            fontWeight: "600",
                            color: "var(--text-muted)"
                          }}>
                            {item.category}
                          </span>
                        </div>
                      </div>
                      
                      {/* Status Badge */}
                      {item.low_stock ? (
                        <div style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                          padding: "6px 12px",
                          borderRadius: "20px",
                          background: "#fee2e2",
                          color: "#ef4444",
                          fontSize: "0.7rem",
                          fontWeight: "700",
                          textTransform: "uppercase",
                          flexShrink: 0
                        }}>
                          <FiAlertTriangle size={12} />
                          Low Stock
                        </div>
                      ) : (
                        <div style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                          padding: "6px 12px",
                          borderRadius: "20px",
                          background: "#dcfce7",
                          color: "#16a34a",
                          fontSize: "0.7rem",
                          fontWeight: "700",
                          textTransform: "uppercase",
                          flexShrink: 0
                        }}>
                          <FiCheckCircle size={12} />
                          In Stock
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Item Details */}
                  <div className="gallery-admin-card__content">
                    {/* Quantity & Pricing */}
                    <div style={{
                      padding: "0.75rem",
                      background: "var(--bg-subtle)",
                      borderRadius: "8px",
                      marginBottom: "0.75rem"
                    }}>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.5rem" }}>
                        <span style={{ fontSize: "0.8125rem", color: "var(--text-muted)" }}>Current Stock:</span>
                        <span style={{ 
                          fontSize: "1.125rem", 
                          fontWeight: "800", 
                          color: item.low_stock ? "#ef4444" : "#0F5D4F" 
                        }}>
                          {item.quantity} {item.unit}
                        </span>
                      </div>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.5rem" }}>
                        <span style={{ fontSize: "0.8125rem", color: "var(--text-muted)" }}>Min Level:</span>
                        <span style={{ fontSize: "0.875rem", fontWeight: "600" }}>{item.min_level} {item.unit}</span>
                      </div>
                      <div style={{ height: "1px", background: "var(--border)", margin: "0.5rem 0" }}></div>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.25rem" }}>
                        <span style={{ fontSize: "0.8125rem", color: "var(--text-muted)" }}>
                          Price ({item.pricing_method === "per_meter" ? "per meter" : "per piece"}):
                        </span>
                        <span style={{ fontSize: "0.875rem", fontWeight: "700", color: "#2563eb" }}>
                          {Number(item.buy_price || item.unit_cost || 0).toFixed(2)} ETB
                        </span>
                      </div>
                      <div style={{ display: "flex", justifyContent: "space-between" }}>
                        <span style={{ fontSize: "0.875rem", fontWeight: "600", color: "var(--text-primary)" }}>Total Value:</span>
                        <span style={{ fontSize: "1rem", fontWeight: "800", color: "#059669" }}>
                          {(Number(item.quantity) * Number(item.buy_price || item.unit_cost || 0)).toFixed(2)} ETB
                        </span>
                      </div>
                    </div>

                    {/* Pricing Method Badge */}
                    <div style={{ 
                      display: "flex", 
                      alignItems: "center", 
                      gap: "6px", 
                      padding: "0.5rem",
                      background: "#eff6ff",
                      borderRadius: "6px",
                      marginBottom: "0.75rem",
                      fontSize: "0.8125rem"
                    }}>
                      <FiLayers size={14} style={{ color: "#3b82f6" }} />
                      <span style={{ color: "#1e40af", fontWeight: "500" }}>
                        {pricingLabel(item.pricing_method)}
                      </span>
                    </div>

                    {/* Actions */}
                    <div className="gallery-admin-card__actions">
                      <button 
                        className="btn-icon btn-icon--edit" 
                        type="button" 
                        onClick={() => {
                          setEditingItemId(item.id);
                          setItemForm({ ...item, buy_price: item.buy_price ?? item.unit_cost ?? 0 });
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                      >
                        <FiEdit2 size={16} />
                        Edit
                      </button>
                      <button 
                        className="btn-icon btn-icon--delete" 
                        type="button" 
                        onClick={() => {
                          if (window.confirm(`Delete "${item.name}"?`)) {
                            deleteStockItem(item.id);
                          }
                        }}
                      >
                        <FiTrash2 size={16} />
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </section>

        {/* ── Movement history ── */}
        <section className="panel" style={{ gridColumn: "span 2" }}>
          <div style={{ padding: "1.5rem", borderBottom: "1px solid var(--border)" }}>
            <h3 style={{ margin: "0", fontSize: "1.25rem", fontWeight: "700" }}>Movement History</h3>
            <p style={{ margin: "0.5rem 0 0 0", fontSize: "0.875rem", color: "var(--text-muted)" }}>
              Track stock in/out transactions
            </p>
          </div>

          <div style={{ padding: "1rem" }}>
            {movementList.length === 0 ? (
              <div className="empty-state-modern">
                <FiActivity />
                <p>No stock movements yet</p>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                {movementList.map((movement) => {
                  const relatedItem = items.find(
                    (i) => Number(i.id) === Number(movement.stock_item_id)
                  );
                  const isIn = movement.movement_type === "IN";
                  
                  return (
                    <div 
                      key={movement.id} 
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: "1rem",
                        padding: "1rem",
                        background: "var(--bg-base)",
                        border: "1px solid var(--border)",
                        borderRadius: "8px",
                        borderLeft: `4px solid ${isIn ? "#10b981" : "#ef4444"}`,
                        transition: "all 0.2s ease"
                      }}
                    >
                      {/* Movement Icon */}
                      <div style={{
                        width: "48px",
                        height: "48px",
                        borderRadius: "12px",
                        background: isIn ? "#dcfce7" : "#fee2e2",
                        color: isIn ? "#10b981" : "#ef4444",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0
                      }}>
                        {isIn ? <FiTrendingUp size={24} /> : <FiTrendingDown size={24} />}
                      </div>

                      {/* Movement Details */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.25rem", flexWrap: "wrap" }}>
                          <div style={{
                            padding: "4px 10px",
                            borderRadius: "12px",
                            background: isIn ? "#dcfce7" : "#fee2e2",
                            color: isIn ? "#16a34a" : "#dc2626",
                            fontSize: "0.7rem",
                            fontWeight: "700",
                            textTransform: "uppercase"
                          }}>
                            {movement.movement_type}
                          </div>
                          <span style={{ fontSize: "0.9375rem", fontWeight: "700", color: "var(--text-primary)" }}>
                            {relatedItem?.name || `Item #${movement.stock_item_id}`}
                          </span>
                        </div>
                        
                        {movement.note && (
                          <p style={{ margin: "0 0 0.5rem 0", fontSize: "0.875rem", color: "var(--text-muted)" }}>
                            {movement.note}
                          </p>
                        )}
                        
                        <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap", fontSize: "0.8125rem" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "4px", color: "var(--text-muted)" }}>
                            <FiBox size={14} />
                            <span>
                              <strong style={{ color: "var(--text-primary)" }}>{movement.quantity}</strong>{" "}
                              {relatedItem?.pricing_method === "per_meter" ? "meters" : "pieces"}
                            </span>
                          </div>
                          <div style={{ display: "flex", alignItems: "center", gap: "4px", color: "var(--text-muted)" }}>
                            <FiDollarSign size={14} />
                            <span>
                              <strong style={{ color: "var(--text-primary)" }}>{movement.unit_cost}</strong> ETB/unit
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Actions */}
                      <div style={{ display: "flex", gap: "0.5rem", flexShrink: 0 }}>
                        <button
                          className="btn-icon btn-icon--edit"
                          type="button"
                          style={{ minWidth: "auto", padding: "0.5rem" }}
                          onClick={() => {
                            setEditingMovementId(movement.id);
                            setForm(movement);
                            document.getElementById("movement-form")?.scrollIntoView({ behavior: "smooth" });
                          }}
                        >
                          <FiEdit2 size={16} />
                        </button>
                        <button
                          className="btn-icon btn-icon--delete"
                          type="button"
                          style={{ minWidth: "auto", padding: "0.5rem" }}
                          onClick={() => {
                            if (window.confirm("Delete this movement?")) {
                              deleteMovement(movement.id);
                            }
                          }}
                        >
                          <FiTrash2 size={16} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </section>

        {/* ── Add / Edit stock item ── */}
        <form className="panel form-panel" onSubmit={handleItemSubmit}>
          <h3>{editingItemId ? "Edit stock item" : "Add stock item"}</h3>

          <input
            value={itemForm.name}
            onChange={(e) => setItemForm({ ...itemForm, name: e.target.value })}
            placeholder="Item name"
            required
          />

          <div className="form-row">
            <input
              value={itemForm.category}
              onChange={(e) => setItemForm({ ...itemForm, category: e.target.value })}
              placeholder="Category (e.g. Paper, Substrate)"
              required
            />
            <input
              value={itemForm.unit}
              onChange={(e) => setItemForm({ ...itemForm, unit: e.target.value })}
              placeholder="Unit label (e.g. ream, roll)"
              required
            />
          </div>

          <div className="form-row">
            <div className="form-field">
              <label>Material type</label>
              <select
                value={itemForm.material_type}
                onChange={(e) => setItemForm({ ...itemForm, material_type: e.target.value })}
              >
                {MATERIAL_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t.charAt(0).toUpperCase() + t.slice(1)}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-field">
              <label>Pricing method</label>
              <select
                value={itemForm.pricing_method}
                onChange={(e) => setItemForm({ ...itemForm, pricing_method: e.target.value })}
              >
                {PRICING_METHODS.map((p) => (
                  <option key={p.value} value={p.value}>
                    {p.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-row">
            <input
              type="number"
              min="0"
              value={itemForm.quantity}
              onChange={(e) => setItemForm({ ...itemForm, quantity: Number(e.target.value) })}
              placeholder="Opening quantity"
            />
            <input
              type="number"
              min="0"
              value={itemForm.min_level}
              onChange={(e) => setItemForm({ ...itemForm, min_level: Number(e.target.value) })}
              placeholder="Min level (alert)"
            />
          </div>

          {/* Buy price field */}
          <div className="form-field">
            <label>
              Buy price{" "}
              <span style={{ fontWeight: 400, color: "var(--text-muted, #888)" }}>
                (per {itemForm.pricing_method === "per_meter" ? "meter" : "piece"})
              </span>
            </label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={itemForm.buy_price}
              onChange={(e) =>
                setItemForm({
                  ...itemForm,
                  buy_price: Number(e.target.value),
                  unit_cost: Number(e.target.value),
                })
              }
              placeholder={
                itemForm.pricing_method === "per_meter"
                  ? "e.g. 120.00 per meter"
                  : "e.g. 18.50 per piece"
              }
            />
          </div>

          {/* Live total */}
          {Number(itemForm.quantity) > 0 && Number(itemForm.buy_price) > 0 && (
            <div
              style={{
                background: "var(--surface-alt, #f3f4f6)",
                borderRadius: 6,
                padding: "0.6rem 0.9rem",
                fontSize: "0.88rem",
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <span>Total buy cost</span>
              <strong>
                {(Number(itemForm.quantity) * Number(itemForm.buy_price)).toFixed(2)}
              </strong>
            </div>
          )}

          <button className="primary-button" type="submit">
            {editingItemId ? "Update item" : "Add item"}
          </button>
          {editingItemId && (
            <button
              className="secondary-button"
              type="button"
              onClick={() => {
                setEditingItemId(null);
                setItemForm(EMPTY_ITEM);
              }}
            >
              Cancel
            </button>
          )}
          {itemError && <small className="danger-text">{itemError}</small>}
        </form>

        {/* ── Post movement ── */}
        <form className="panel form-panel" onSubmit={handleSubmit}>
          <h3>{editingMovementId ? "Edit movement" : "Post movement"}</h3>

          <div className="form-field">
            <label>Material</label>
            <select
              value={form.stock_item_id}
              onChange={(e) => setForm({ ...form, stock_item_id: Number(e.target.value) })}
            >
              {items.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name} ({pricingLabel(item.pricing_method)})
                </option>
              ))}
            </select>
          </div>

          <div className="form-row">
            <div className="form-field">
              <label>Direction</label>
              <select
                value={form.movement_type}
                onChange={(e) => setForm({ ...form, movement_type: e.target.value })}
              >
                <option value="IN">IN — Purchase / Restock</option>
                <option value="OUT">OUT — Used in production</option>
              </select>
            </div>
            <div className="form-field">
              <label>
                Quantity (
                {items.find((i) => Number(i.id) === Number(form.stock_item_id))
                  ?.pricing_method === "per_meter"
                  ? "meters"
                  : "pieces"}
                )
              </label>
              <input
                type="number"
                min="0.01"
                step="0.01"
                value={form.quantity}
                onChange={(e) => setForm({ ...form, quantity: Number(e.target.value) })}
              />
            </div>
          </div>

          <input
            type="number"
            min="0"
            step="0.01"
            value={form.unit_cost}
            onChange={(e) => setForm({ ...form, unit_cost: Number(e.target.value) })}
            placeholder="Unit cost at time of movement"
          />
          <textarea
            value={form.note}
            onChange={(e) => setForm({ ...form, note: e.target.value })}
            placeholder="Movement note (e.g. Allocated to order #5)"
            rows="3"
          />

          <button className="primary-button" type="submit">
            {editingMovementId ? "Update movement" : "Save movement"}
          </button>
          {editingMovementId && (
            <button
              className="secondary-button"
              type="button"
              onClick={() => {
                setEditingMovementId(null);
                setForm({ ...EMPTY_MOVEMENT, stock_item_id: items[0]?.id || "" });
              }}
            >
              Cancel
            </button>
          )}
          {submitError && <small className="danger-text">{submitError}</small>}
        </form>
      </div>
    </div>
  );
};

export default StockPage;
